import React, { createContext, useContext, useState, useEffect } from 'react';

const GUEST_SESSION_MS = 10 * 60 * 1000;   // 10 minutes
const GUEST_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h before guest can be used again

const getStoredGuestExpiry = () => {
    try {
        const s = localStorage.getItem('guestSessionExpiry');
        return s ? parseInt(s, 10) : null;
    } catch { return null; }
};
const getGuestCooldownUntil = () => {
    try {
        const s = localStorage.getItem('guestCooldownUntil');
        return s ? parseInt(s, 10) : null;
    } catch { return null; }
};

/** True if this browser has ever used guest login — guest is allowed only once. */
const hasEverUsedGuest = () => {
    try {
        return localStorage.getItem('guestEverUsed') === '1' || localStorage.getItem('guestUsedAt') != null;
    } catch { return false; }
};

const AppContext = createContext();

export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const stored = getStoredGuestExpiry();
        if (stored && Date.now() < stored) {
            return { role: 'guest', name: 'Guest User' };
        }
        try {
            const raw = localStorage.getItem('user');
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return null;
    });

    const [settings, setSettings] = useState(() => {
        let initialTheme = 'dark';
        try {
            initialTheme = localStorage.getItem('tv_theme') || 'dark';
        } catch (_) {}
        return {
            unit: 'ft',
            theme: initialTheme,
            language: 'en'
        };
    });

    // Synchronize active theme with document, body, localStorage, and meta tag
    useEffect(() => {
        const activeTheme = settings?.theme || 'dark';
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', activeTheme);
            document.body.setAttribute('data-theme', activeTheme);
            if (activeTheme === 'dark') {
                document.documentElement.classList.add('dark');
                document.body.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
                document.body.classList.remove('dark');
            }
            const metaTheme = document.querySelector('meta[name="theme-color"]');
            if (metaTheme) {
                metaTheme.setAttribute('content', activeTheme === 'dark' ? '#090d16' : '#ffffff');
            }
        }
        try {
            localStorage.setItem('tv_theme', activeTheme);
        } catch (_) {}
    }, [settings?.theme]);

    const [currentProject, setCurrentProject] = useState(null);
    const [guestSessionExpiry, setGuestSessionExpiry] = useState(getStoredGuestExpiry);

    // Rehydrate guest session or regular user from localStorage so reload survives
    useEffect(() => {
        const stored = getStoredGuestExpiry();
        if (stored && Date.now() < stored) {
            setGuestSessionExpiry(stored);
            setUser({ role: 'guest', name: 'Guest User' });
        } else {
            setGuestSessionExpiry(null);
            try {
                const raw = localStorage.getItem('user');
                if (raw) {
                    setUser(JSON.parse(raw));
                    return;
                }
            } catch (_) {}
            setUser(null);
        }
    }, []);

    const login = (role) => {
        setUser({ role, name: role === 'guest' ? 'Guest User' : role.charAt(0).toUpperCase() + role.slice(1) });
        if (role === 'guest') {
            const expiry = Date.now() + GUEST_SESSION_MS;
            setGuestSessionExpiry(expiry);
            try {
                localStorage.setItem('guestSessionExpiry', String(expiry));
                localStorage.setItem('guestUsedAt', String(Date.now()));
                localStorage.setItem('guestEverUsed', '1'); // block guest option on future visits
            } catch (_) { }
        } else {
            setGuestSessionExpiry(null);
            try { localStorage.removeItem('guestSessionExpiry'); } catch (_) { }
        }
    };

    const logout = () => {
        setUser(null);
        setGuestSessionExpiry(null);
        try {
            localStorage.clear();
            sessionStorage.clear();
        } catch (_) { }
    };

    // When guest session expires, set cooldown and clear session so they cannot use guest again for 24h
    useEffect(() => {
        if (!guestSessionExpiry || user?.role !== 'guest') return;
        if (Date.now() >= guestSessionExpiry) {
            try {
                localStorage.setItem('guestCooldownUntil', String(Date.now() + GUEST_COOLDOWN_MS));
                localStorage.removeItem('guestSessionExpiry');
            } catch (_) { }
            setGuestSessionExpiry(null);
            setUser(null);
        }
    }, [guestSessionExpiry, user?.role]);

    const isGuestSessionValid = () => {
        if (!guestSessionExpiry) return false;
        return Date.now() < guestSessionExpiry;
    };

    const isGuestCooldown = () => {
        const until = getGuestCooldownUntil();
        return until != null && Date.now() < until;
    };

    /** Guest was used once in this browser; do not show guest option again. */
    const guestAlreadyUsed = hasEverUsedGuest;

    const value = {
        user,
        settings,
        guestSessionExpiry,
        isGuestSessionValid,
        isGuestCooldown,
        guestAlreadyUsed,
        setSettings,
        currentProject,
        setCurrentProject,
        login,
        logout
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within an AppProvider');
    }
    return context;
};
